'use client'

import { CSSProperties, forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '../../utils'
import { useMessageMentions } from '../chat/input/mentions/atoms'
import { useGlobalEvent } from '@highlight/ipc/react'
import {
  getPlainTextCaretPosition,
  restorePlainTextCaretPosition,
  useMacOsAutocorrectFix,
} from '../../../hooks/use-mac-os-autocorrect-fix'

// We mock on change event data
export type MentionableChangeEvent = {
  target: {
    value: string
    selectionStart: number
  }
}

// We mock selection event data
export type MentionableSelectionEvent = {
  target: {
    selectionStart: number
  }
}

type Props = {
  id?: string
  className?: string
  placeholderClassName?: string
  wrapperClassName?: string
  autoFocus?: boolean
  disabled?: boolean
  placeholder?: string | React.ReactElement
  value: string
  onChange: (event: MentionableChangeEvent) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  onSelect?: (event: MentionableSelectionEvent) => void
  onPaste?: (event: React.ClipboardEvent<HTMLElement>) => void | boolean | string[] | Promise<void | boolean | string[]>
}

export const MentionableInput = memo(
  forwardRef<HTMLElement, Props>(
    (
      {
        wrapperClassName,
        className,
        placeholderClassName,
        id,
        autoFocus,
        disabled,
        placeholder,
        value,
        onChange,
        onKeyDown,
        onSelect,
        onPaste,
      },
      forwardedRef,
    ) => {
      const localRef = useRef<HTMLDivElement | null>(null)
      const editorRef = (forwardedRef || localRef) as React.MutableRefObject<HTMLDivElement | null>
      const undoStack = useRef<string[]>([])
      const redoStack = useRef<string[]>([])
      const mentions = useMessageMentions()
      const macOsAutocorrect = useMacOsAutocorrectFix(editorRef)

      useGlobalEvent('chatInput:moveCursorToEnd', (mentionableInputId: string) => {
        if (mentionableInputId === id) {
          placeCaretAtEnd()
        }
      })

      // Track if IME composition is in progress.
      const [isComposing, setIsComposing] = useState(false)

      // Get plain text from the editor (removing zero-width chars).
      const getPlainText = () => {
        const plaintext = editorRef.current?.innerText.replace(/\u200B/g, '') ?? ''
        if (plaintext === '\n') {
          return ''
        }
        return plaintext
      }

      const getCaretPosition = () => {
        const el = editorRef.current
        return el ? getPlainTextCaretPosition(el) : 0
      }

      // Insert raw text at cursor, used by paste & other flows.
      const insertTextAtCursor = (text: string) => {
        const sel = window.getSelection()
        if (!sel || !sel.rangeCount) {
          return
        }
        sel.deleteFromDocument()
        const range = sel.getRangeAt(0)
        const textNode = document.createTextNode(text)
        range.insertNode(textNode)
        range.setStartAfter(textNode)
        range.setEndAfter(textNode)
        sel.removeAllRanges()
        sel.addRange(range)
        handleInput() // reflect changes

        // scroll to bottom of input after inserting text
        if (editorRef.current) {
          editorRef.current.scrollTop = editorRef.current.scrollHeight
        }
      }

      // Called when user pastes text
      const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault()
        if (disabled) {
          return
        }

        if (typeof onPaste === 'function') {
          const handled = await onPaste(e)
          if (Array.isArray(handled)) {
            for (const text of handled) {
              insertTextAtCursor(text)
            }
          } else if (!handled) {
            const clipboardData = e.clipboardData

            // Iterate over all items in the clipboard
            for (let i = 0; i < clipboardData.items.length; i++) {
              const item = clipboardData.items[i]

              // Check if the item is of type string and specifically plain text
              if (item.kind === 'string' && item.type === 'text/plain') {
                // getAsString is asynchronous and returns the text via a callback
                item.getAsString((text: string) => {
                  insertTextAtCursor(text)
                })
              }
            }
          }
        } else {
          insertTextAtCursor(await navigator.clipboard.readText())
        }
      }

      // Replaces mentions in `text` with <span> elements and sets .innerHTML.
      const parseAndSetContent = (updatedContent: string) => {
        if (!editorRef.current) {
          return
        }

        saveToUndoStack() // Save current state
        const caretPosition = getCaretPosition()

        // Escape HTML to prevent injection
        updatedContent = updatedContent
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\u200B/g, '')

        // Insert mention spans
        let newContent = updatedContent
        const hasMentions = mentions.length > 0

        mentions.forEach((mention) => {
          const escapedLabel = mention.label.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
          // Slash mentions only match at start or after whitespace, so URLs containing the label
          // aren't falsely styled. `@` mentions match anywhere (matches existing behavior).
          const mentionRegex = new RegExp(`(?:(?<=^|\\s)(/)|(@))${escapedLabel}\\b`, 'g')
          newContent = newContent.replace(
            mentionRegex,
            (_match, slashPrefix, atPrefix) =>
              `<span class="font-medium text-info-subtle" data-value="${mention.value}">${slashPrefix || atPrefix}${mention.label}</span>`,
          )
        })

        if (!newContent) {
          newContent = '\u200B'
        }

        editorRef.current.innerHTML = newContent

        // If there are mentions, specifically ensure height expands correctly
        if (hasMentions) {
          // Remove height constraints immediately
          editorRef.current.style.maxHeight = 'none'
          editorRef.current.style.height = 'auto'
          editorRef.current.style.overflowY = 'visible'

          // Force reflow
          void editorRef.current.offsetHeight
        }

        const newPlainText = getPlainText()
        // Only restore caret if editor is focused — setting selection can implicitly focus
        if (editorRef.current.contains(document.activeElement)) {
          restorePlainTextCaretPosition(editorRef.current, Math.min(newPlainText.length, caretPosition))
        }
      }

      // Save current .innerHTML to the undo stack
      const saveToUndoStack = () => {
        if (editorRef.current) {
          const previous = undoStack.current.slice(-1).pop()
          const current = getPlainText()
          if (previous === current) {
            return
          }
          undoStack.current.push(current)
          // Clear redo stack on new input
          redoStack.current = []
        }
      }

      // Basic Undo
      const handleUndo = () => {
        if (undoStack.current.length > 1) {
          redoStack.current.push(undoStack.current.pop()!)
          const lastState = undoStack.current[undoStack.current.length - 1]
          setContentAndEmitChange(lastState)
          placeCaretAtEnd()
        }
      }

      // Basic Redo
      const handleRedo = () => {
        if (redoStack.current.length > 0) {
          const nextState = redoStack.current.pop()!
          undoStack.current.push(nextState)
          setContentAndEmitChange(nextState)
          placeCaretAtEnd()
        }
      }

      // Sets .innerHTML and fires onChange with new plain text
      const setContentAndEmitChange = (updatedHTML: string) => {
        if (!editorRef.current) {
          return
        }
        editorRef.current.innerHTML = updatedHTML

        const text = getPlainText()
        const caretPos = getCaretPosition()

        onChange({
          target: {
            value: text,
            selectionStart: caretPos,
          },
        })
      }

      // Called on every input event
      const handleInput = () => {
        if (disabled || !editorRef.current) {
          return
        }

        // Update the parent's state with raw text
        const text = getPlainText()
        const caretPos = getCaretPosition()

        // Check if we have mentions and adjust height immediately
        if (mentions.length > 0) {
          // When mentions are present, remove height constraints
          editorRef.current.style.maxHeight = 'none'
          editorRef.current.style.height = 'auto'
          editorRef.current.style.overflowY = 'visible'

          // Force layout recalculation
          requestAnimationFrame(() => {
            if (editorRef.current) {
              // Ensure parent containers are notified of size change
              const resizeEvent = new CustomEvent('resize', {
                bubbles: true,
                detail: { isMultiline: true, hasMentions: true },
              })
              editorRef.current.dispatchEvent(resizeEvent)
            }
          })
        }

        // Check for @ character to improve mention detection
        if (text.includes('@')) {
          const sel = window.getSelection()
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0)
            const container = range.startContainer

            // If @ was just inserted, we want to be sure it triggers mention detection
            if (container.nodeType === Node.TEXT_NODE && container.textContent && container.textContent.includes('@')) {
              const textBeforeCursor = container.textContent.substring(0, range.startOffset)
              if (textBeforeCursor.endsWith('@')) {
                console.log('@ detected in input handler, triggering change event with cursor at @')
              }
            }
          }
        }

        onChange({ target: { value: text, selectionStart: caretPos } })

        // Only parse & set mentions if NOT composing
        if (!isComposing) {
          parseAndSetContent(text)
        }
      }

      // Composition event handlers
      const handleCompositionStart = () => {
        setIsComposing(true)
      }
      const handleCompositionUpdate = () => {
        // Typically do nothing; the IME is controlling the text
      }
      const handleCompositionEnd = () => {
        setIsComposing(false)
        // Now that composition is done, parse the final text
        handleInput()
      }

      // Track selection changes
      const handleSelectionChange = () => {
        if (!editorRef.current || !onSelect) {
          return
        }
        onSelect({ target: { selectionStart: getCaretPosition() } })
      }

      // Move caret to end of content
      const placeCaretAtEnd = () => {
        const el = editorRef.current
        if (!el) {
          return
        }
        el.focus()
        const range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        const sel = window.getSelection()
        if (sel) {
          sel.removeAllRanges()
          sel.addRange(range)
        }
      }

      // Key handling
      const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
          return
        }

        if (onKeyDown) {
          onKeyDown(e)
        }

        if (e.isDefaultPrevented()) {
          return
        }

        if (
          (e.metaKey || e.ctrlKey) && // Cmd (Mac) or Ctrl (Windows/Linux)
          e.shiftKey && // Shift key
          e.key.toLowerCase() === 'v' // 'V' key
        ) {
          e.preventDefault()

          try {
            const text = await navigator.clipboard.readText()
            insertTextAtCursor(text)
          } catch (err) {
            console.error('Failed to read clipboard contents:', err)
          }
        }

        // Finish composition on Enter
        if (isComposing && e.key === 'Enter') {
          e.preventDefault()
          // Force composition to end by blurring
          editorRef.current?.blur()
          return
        }

        // Undo/Redo
        if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
          // Right after an accepted macOS autocorrect, the system holds a
          // native "Change Back to …" undo action. Running the JS undo
          // (which rewrites innerHTML) while that action is pending wedges
          // the window's text input context and swallows keystrokes — so
          // let that one cmd+Z fall through to the native undo. Chromium's
          // own editing history is usually empty here (we rewrite innerHTML
          // on every input), so if the keystroke settles without changing
          // anything, apply the JS undo then.
          if (macOsAutocorrect.shouldDeferUndoToNative()) {
            const textBeforeNativeUndo = getPlainText()
            setTimeout(() => {
              if (getPlainText() === textBeforeNativeUndo) {
                handleUndo()
              }
            }, 100)
            return
          }
          e.preventDefault()
          handleUndo()
        }
        if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
          e.preventDefault()
          handleRedo()
        }
      }

      // On blur, finalize composition if needed
      const handleBlur = () => {
        if (isComposing) {
          setIsComposing(false)
          handleInput()
        }
      }

      // Prevent focus if disabled
      const onFocus = useCallback(
        (e: React.FocusEvent<HTMLElement>) => {
          if (disabled) {
            e.target.blur()
          } else {
            placeCaretAtEnd()
          }
        },
        [disabled],
      )

      // If the parent `value` changes externally, update only if not composing
      useEffect(() => {
        if (!isComposing) {
          parseAndSetContent(value)
        }
      }, [value, isComposing])

      // If mentions change, re-parse only if not composing
      useEffect(() => {
        if (!isComposing) {
          parseAndSetContent(getPlainText())
          // Only place caret if the editor is already focused — don't steal focus
          if (editorRef.current && editorRef.current.contains(document.activeElement)) {
            placeCaretAtEnd()
          }
        }
      }, [mentions, isComposing])

      // Autofocus
      useEffect(() => {
        if (autoFocus) {
          editorRef.current?.focus()
        }
      }, [autoFocus])

      // Unfocus if disabled
      useEffect(() => {
        if (disabled) {
          editorRef.current?.blur()
        }
      }, [disabled])

      // Handle selection on mount (for a pre-filled value)
      useEffect(() => {
        handleSelectionChange()
      }, [])

      return (
        <div
          className={cn(
            'text-md relative w-fit max-w-full flex-grow',
            wrapperClassName,
            disabled ? 'pointer-events-none cursor-not-allowed opacity-60' : '',
          )}
        >
          {/* Hide placeholder if we have text or if IME is composing */}
          {placeholder && !isComposing && value === '' && (
            <span
              // The live dictation transcript rides in on the placeholder and
              // outgrows the composer, so the box is capped at the width left
              // beside the action buttons and clips. Below that cap it
              // shrink-to-fits, so an ordinary placeholder still sits flush
              // left; past it, the RTL box keeps the newest words at the right
              // edge and clips the stale head — `text-align: right` can't do
              // this, browsers pin an overflowing line to its start edge. The
              // inner `dir` keeps the transcript itself ordered LTR, so
              // trailing punctuation doesn't jump to the front.
              dir="rtl"
              className={cn(
                'text-foreground-secondary pointer-events-none absolute left-0 select-none overflow-hidden whitespace-nowrap',
                // The wrapper's min-height can exceed one line, and
                // `justify-center` centres the editor inside it, so a
                // top-pinned placeholder sits above the caret. Centring tracks
                // that, and is a no-op where the wrapper is one line tall.
                'top-1/2 -translate-y-1/2',
                // Padding, offset by the zeroed insets so the text still sits
                // at 4px, keeps the placeholder's blur-in inside the clip.
                'max-w-[calc(100%-0.5rem)] p-1',
                placeholderClassName,
              )}
            >
              <span dir="ltr">{placeholder}</span>
            </span>
          )}

          <div
            ref={editorRef}
            id={id}
            className={cn(
              'text-foreground select-text overflow-y-auto whitespace-pre-wrap p-1 outline-none',
              'max-h-[var(--mentionable-input-max-height,300px)]',
              className,
              disabled && 'pointer-events-none cursor-not-allowed',
            )}
            style={overflowWrap}
            contentEditable
            onInput={handleInput}
            onPaste={handlePaste}
            onKeyUp={handleSelectionChange}
            onKeyDown={handleKeyDown}
            onMouseUp={handleSelectionChange}
            onFocus={onFocus}
            onBlur={handleBlur}
            onCompositionStart={handleCompositionStart}
            onCompositionUpdate={handleCompositionUpdate}
            onCompositionEnd={handleCompositionEnd}
            spellCheck={false}
          />
        </div>
      )
    },
  ),
)
const overflowWrap: CSSProperties = {
  overflowWrap: 'break-word',
}
